<i18n>
{
	"en": {
		"header": "Order Templates",
		"noTemplates": "No Order Templates Found",
		"editTemplate": "Edit Template",
		"deleteTemplate": "Delete Order Template | Delete Order Template | Delete {count} Order Templates",
		"newTemplate": "New Order Template",
		"failedToFetch": "Failed to fetch order templates",
		"failedToDelete": "Failed to delete order template",
		"name": "Name",
		"economicalStatus": "Economical status",
		"incomplete": "Incomplete",
		"notSettled": "Not settled",
		"settled": "Settled",
		"invoiced": "Invoiced",
		"recorded": "Recorded",
		"operationalStatus": "Status",
		"orderType": "Order type",
		"clerk": "Clerk",
		"customer": "Customer",
		"consignor": "Consignor",
		"consignee": "Consignee",
		"despatchParty": "Despatch party",
		"deliveryParty": "Delivery party",
		"customerName": "Customer name",
		"cancelled": "Cancelled",
		"draft": "Draft",
		"received": "Received",
		"rejected": "Rejected",
		"ready": "Ready",
		"planned": "Planned",
		"unsuccessful": "Unsuccessful",
		"requested": "Requested",
		"accepted": "Accepted",
		"loaded": "Loaded",
		"unloaded": "Unloaded",
		"generateOrders": "Generate orders...",
		"autoSchedule": "Auto",
		"duration": "Duration",
		"days": "Day | Days",
		"yes": "Yes",
		"no": "No",
		"nextOrder": "Next generated order"
	},
	"no": {
		"header": "Oppdragsmaler",
		"noTemplates": "Fant Ingen Oppdragsmaler",
		"editTemplate": "Rediger Oppdragsmal",
		"deleteTemplate": "Slett Oppdragsmal | Slett Oppdragsmal | Slett {count} Oppdragsmaler",
		"newTemplate": "Ny Oppdragsmal",
		"failedToFetch": "Kunne ikke hente oppdragsmaler",
		"failedToDelete": "Kunne ikke slette oppdragsmal",
		"name": "Navn",
		"economicalStatus": "Økonomisk status",
		"incomplete": "Ufullstendig",
		"notSettled": "Ikke klarert",
		"settled": "Klarert",
		"invoiced": "Oppgjort",
		"recorded": "Bokført",
		"operationalStatus": "Status",
		"received": "Mottatt",
		"orderType": "Oppdragstype",
		"clerk": "Ansvarlig",
		"customer": "Kunde",
		"consignor": "Avsender",
		"consignee": "Mottaker",
		"despatchParty": "Fra adresse",
		"deliveryParty": "Til adresse",
		"customerName": "Kundenavn",
		"cancelled": "Annullert",
		"draft": "Kladd",
		"received": "Mottatt",
		"rejected": "Avslått",
		"ready": "Klar",
		"planned": "Planlagt",
		"unsuccessful": "Bomtur",
		"requested": "Forespurt",
		"accepted": "Akseptert",
		"loaded": "Lastet",
		"unloaded": "Losset",
		"generateOrders": "Generer oppdrag...",
		"autoSchedule": "Auto",
		"duration": "Varighet",
		"days": "Dag | Dager",
		"yes": "Ja",
		"no": "Nei",
		"nextOrder": "Neste genererte oppdrag"
	}
}
</i18n>
<template>
	<t-vertical-panel class="order-templates">
		<template #header>
			<div class="order-templates-header">
				<t-panel-header size="md" icon="order">
					{{ $t('header') }}
				</t-panel-header>
			</div>
		</template>
		<template #sub-header>
			<div class="order-templates-sub-header">
				<div class="button-group">
					<t-button
						:disabled="rowsSelected !== 1"
						iconBefore="annotation"
						appearance="minimal"
						@click="editTemplate()"
					>{{ $t('editTemplate') }}</t-button>
					<t-button
						iconBefore="add-to-artifact"
						@click="autoScheduleOrders"
					>{{ $t('generateOrders') }}</t-button>
				</div>
				<div class="button-group right">
					<t-button
						:disabled="rowsSelected === 0"
						iconBefore="trash"
						appearance="minimal"
						@click="deleteTemplate"
					>{{ $tc('deleteTemplate', rowsSelected) }}</t-button>
					<t-button
						iconBefore="plus"
						appearance="primary"
						@click="newTemplate"
					>{{ $t('newTemplate') }}</t-button>
				</div>
			</div>
		</template>
		<div class="order-templates-content">
			<t-grid
				v-if="orderTemplates.length"
				ref="grid"
				:columnDefinitions="columnDefinitions"
				:defaultColumnDefinition="defaultColumnDefinition"
				:rowHeight="50"
				:items="orderTemplates"
				selectable
				@selected="onRowsSelected"
				@rowDoubleClick="editTemplate"
				@enter="editTemplate()"
			>
				<template #duration="{ item }">
					{{ item.duration }} {{ $tc('days', item.duration) }}
				</template>
				<template #autoSchedule="{ item }">
					{{ item.autoSchedule ? $t('yes') : $t('no') }}
				</template>
				<template #status="{ item }">
					<t-timpex-icon
						:icon="getStatusIcon(item.goodsType)"
						:color="getStatusColor(item.status)"
						size="md"
					/>
				</template>
				<template #consignor="{ item: { consignor } }">
					<div style="height: 16px;">{{ consignor.name }}</div>
					<div
						class="grid-cell-sub-line"
						:class="{'missing-geo-data': !consignor.latitude || !consignor.longitude }"
					>
						{{ consignor.street }}, {{ consignor.postalCode }} {{ consignor.postalPlace }}
					</div>
				</template>
				<template #consignee="{ item: { consignee } }">
					<div style="height: 16px;">{{ consignee.name }}</div>
					<div
						class="grid-cell-sub-line"
						:class="{'missing-geo-data': !consignee.latitude || !consignee.longitude }"
					>
						{{ consignee.street }}, {{ consignee.postalCode }} {{ consignee.postalPlace }}
					</div>
				</template>
				<template #despatchParty="{ item: { despatchParty } }">
					<div style="height: 16px;">{{ despatchParty.name }}</div>
					<div
						class="grid-cell-sub-line"
						:class="{'missing-geo-data': !despatchParty.latitude || !despatchParty.longitude }"
					>
						{{ despatchParty.street }}, {{ despatchParty.postalCode }} {{ despatchParty.postalPlace }}
					</div>
				</template>
				<template #deliveryParty="{ item: { deliveryParty } }">
					<div style="height: 16px;">{{ deliveryParty.name }}</div>
					<div
						class="grid-cell-sub-line"
						:class="{'missing-geo-data': !deliveryParty.latitude || !deliveryParty.longitude }"
					>
						{{ deliveryParty.street }}, {{ deliveryParty.postalCode }} {{ deliveryParty.postalPlace }}
					</div>
				</template>
				<template #customer="{ item }">
					<div v-if="item.customer" style="height: 16px;">{{ item.customer.name }}</div>
					<div v-if="item.customer" class="grid-cell-sub-line">{{ item.customer.customerNo }}</div>
				</template>
				<template #economicalStatus="{ item: { economicalStatus }, value }">
					<t-status-color-dot :color="getStatusColor(economicalStatus)" />
					{{ value }}
				</template>
			</t-grid>

			<t-no-content-panel v-else :text="$t('noTemplates')" icon="order" />
			<t-overlay
				:isShown="loading"
				absolute
				loader
			/>
		</div>

		<auto-schedule-dialog
			:isShown="showAutoScheduleDialog"
			:selectedTemplates="selectedTemplates"
			@close="autoScheduleClosed"
			@ok="getOrderTemplates(true)"
		/>
	</t-vertical-panel>
</template>
<script>
import { eventBus, statuses, goodsTypesIcons, displayDateFormatter } from '@/core';
import { mapState, mapActions } from './store/orderTemplates.store';
import AutoScheduleDialog from './AutoScheduleDialog.vue';

export default {
	name: 'OrderTemplates',
	components: { AutoScheduleDialog },
	data() {
		return {
			defaultColumnDefinition: {
				resizable: true,
				headerFilterInputWithButton: true
			},
			rowsSelected: 0,
			selectedTemplates: [],
			showAutoScheduleDialog: false
		};
	},
	computed: {
		...mapState([
			'orderTemplates',
			'loading'
		]),
		columnDefinitions() {
			return [
				{
					field: 'name',
					displayName: this.$t('name'),
					minWidth: 100
				},
				{
					field: 'duration',
					displayName: this.$t('duration'),
					width: 70,
					align: 'center'
				},
				{
					field: 'autoSchedule',
					displayName: this.$t('autoSchedule'),
					valueGetter: (item) => item.autoSchedule,
					filterable: false,
					align: 'center',
					width: 50
				},
				{
					field: 'status',
					displayName: this.$t('operationalStatus'),
					valueGetter: ({ status }) => this.$t(this.getOrderStatus(status)),
					align: 'center',
					width: 80
				},
				{
					field: 'customer',
					displayName: this.$t('customerName'),
					comparableValueGetter: ({ customer }) => customer ? `${customer.name} ${customer.customerNo}` : '',
					minWidth: 125,
					type: 'customer',
				},
				{
					field: 'consignor',
					displayName: this.$t('consignor'),
					valueGetter: ({ consignor }) => consignor.name,
					comparableValueGetter: ({ consignor }) => this.addressGetter(consignor),
					minWidth: 200,
				},
				{
					field: 'consignee',
					displayName: this.$t('consignee'),
					valueGetter: ({ consignee }) => consignee.name,
					comparableValueGetter: ({ consignee }) => this.addressGetter(consignee),
					minWidth: 200,
				},
				{
					field: 'despatchParty',
					displayName: this.$t('despatchParty'),
					valueGetter: ({ despatchParty }) => despatchParty.name,
					comparableValueGetter: ({ despatchParty }) => this.addressGetter(despatchParty),
					minWidth: 200,
				},
				{
					field: 'deliveryParty',
					displayName: this.$t('deliveryParty'),
					valueGetter: ({ deliveryParty }) => deliveryParty.name,
					comparableValueGetter: ({ deliveryParty }) => this.addressGetter(deliveryParty),
					minWidth: 200,
				},
				{
					field: 'orderType',
					displayName: this.$t('orderType'),
					valueGetter: ({ orderType }) => orderType && 'name' in orderType ? orderType.name : '',
					minWidth: 125
				},
				{
					field: 'economicalStatus',
					displayName: this.$t('economicalStatus'),
					valueGetter: ({ economicalStatus }) => this.$t(this.getEconomicalStatus(economicalStatus)),
					width: 125
				},
				{
					field: 'clerk',
					displayName: this.$t('clerk'),
					valueGetter: ({ clerk }) => clerk && 'name' in clerk ? clerk.name : '',
					minWidth: 125
				},
				{
					field: 'nextOrder',
					displayName: this.$t('nextOrder'),
					valueGetter: (item) => item.nextOrder && item.autoSchedule ? this.displayDate(item.nextOrder) : '',
					align: 'center',
					width: 160,
				}
			];
		}
	},
	async created() {
		try {
			await this.getOrderTemplates();
		} catch (err) {
			this.$notify({
				type: 'error',
				title: this.$t(err.message)
			});
		}
	},
	methods: {
		...mapActions([
			'getOrderTemplates',
			'deleteTemplates',
		]),
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		getEconomicalStatus(status) {
			return statuses.economical(status);
		},
		getOrderStatus(status) {
			return statuses.order(status);
		},
		getStatusColor(status) {
			return statuses.color(status);
		},
		getStatusIcon(goodsType) {
			return goodsTypesIcons.getById( goodsType && 'iconId' in goodsType ? goodsType.iconId : -1);
		},
		displayDate(storageDate) {
			return displayDateFormatter(storageDate);
		},
		addressGetter(party) {
			if (!party) return '';
			return `${party.name} ${party.street} ${party.postalCode} ${party.postalPlace}`;
		},
		newTemplate() {
			eventBus.$emit('toggleOrderTemplateDetailsModal', true);
		},
		editTemplate(id) {
			let template = undefined;
			if (id) {
				template = this.orderTemplates.find( (x) => x.id === id );
			} else {
				let selectedItems = this.$refs.grid.getSelectedItems();
				if (selectedItems.length > 1) return;
				[template] = selectedItems;
			}
			eventBus.$emit('setOrderTemplateDetails', template);
			eventBus.$emit('toggleOrderTemplateDetailsModal', true);
		},
		autoScheduleOrders() {
			this.selectedTemplates = this.$refs.grid.getSelectedItems();
			this.showAutoScheduleDialog = true;
		},
		autoScheduleClosed() {
			this.selectedTemplates = [];
			this.showAutoScheduleDialog = false;
		},
		async deleteTemplate() {
			let items = this.$refs.grid.getSelectedItems().map( (x) => x.id );
			try {
				await this.deleteTemplates(items);
				this.rowsSelected = 0;
				if (this.$refs.grid) this.$refs.grid.resetSelectedRows();
			} catch (err) {
				this.$notify({
					type: 'error',
					title: this.$t(err.message)
				});
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.order-templates {
	.order-templates-header {
		height: 100%;
		display: flex;
		align-items: center;
		padding-left: 15px;
	}
	.order-templates-sub-header {
		display: flex;
		padding: 0px 15px;
		height: 100%;
		align-items: center;
		justify-content: space-between;

		.button-group {
			display: flex;
			height: 100%;
			align-items: center;
			justify-content: flex-end;
			> button {
				margin-left: 20px;
			}
		}
	}
	.order-templates-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px;
	}
}
</style>
